import type { UseComboboxStateChangeTypes } from 'downshift';
import { useCombobox } from 'downshift';

import type { IOptions } from '../../../shared/utils/options';
import type { TInputDropdownEventType } from '../../../shared/utils/types';

import type { IInputDropdownProps } from '../_props';

/**
 * Typings
 */

type TUseSelectItem = {
  filterable?: boolean;
  options: IOptions[];
  isSelect?: boolean;
};

type THandleSelectItem = {
  selectedItem?: IOptions | null;
  setListOptions: (listOptions: IOptions[]) => void;
};

type TPropagateSelectItem = {
  selectedItem?: IOptions | null;
  type: UseComboboxStateChangeTypes;
} & Pick<IInputDropdownProps, 'onChangeSelect'>;

/**
 * Dropdown Actions Map
 */

const dropdownAction = new Map<UseComboboxStateChangeTypes, TInputDropdownEventType>([
  [useCombobox.stateChangeTypes.InputKeyDownEnter, 'Select'],
  [useCombobox.stateChangeTypes.ItemClick, 'Select'],
  [useCombobox.stateChangeTypes.InputBlur, 'Tab'],
]);

/**
 * Hook
 */

export const useSelectItem = ({ filterable, options, isSelect }: TUseSelectItem) => {
  /**
   * Handlers
   */

  const handleSelectItem = ({ selectedItem, setListOptions }: THandleSelectItem) => {
    if (!filterable || isSelect) return;

    setListOptions(
      options.filter((option) => option.label.toLowerCase().includes(selectedItem?.label.toLowerCase() || '')),
    );
  };

  const propagateSelectItem = ({ selectedItem, onChangeSelect, type }: TPropagateSelectItem) => {
    if (!selectedItem) return;

    onChangeSelect?.(selectedItem, dropdownAction.get(type));
  };

  /**
   * Return
   */

  return { handleSelectItem, propagateSelectItem };
};

export default useSelectItem;
